import { defineStore } from 'pinia';
import axios from 'axios';
import { apiBaseUrl } from '@/config';
import router from '@/router/index';
import { getDynamicLinkById } from '@/fdl';
import { logger } from '@/utils/logger';

function parseJwtPayload(token) {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return {};
  }
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: localStorage.getItem('user-token') || '',
    current_user: null,
    is_staff_verified: Boolean(parseJwtPayload(localStorage.getItem('user-token') || '').is_staff),
  }),
  getters: {
    isAuthenticated: (state) => {
      return state.token && state.token.trim().length > 0;
    },
    isDemoAccount: (state) => {
      return state.current_user?.demo_account === 1;
    },
    isLoggedin: (state) => {
      return Boolean(state.current_user?.login_id);
    }
  },
  actions: {
    async authenticateUser(token) {
      this.token = token;
      localStorage.setItem('user-token', token);
      const payload = parseJwtPayload(token);
      this.is_staff_verified = Boolean(payload.is_staff);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      await this.fetchCurrentUser();
    },
    async fetchCurrentUser() {
      if (this.isAuthenticated) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${this.token}`;
          const response = await axios.get(`${apiBaseUrl}/api/users/me`);
          this.current_user = response.data;
        } catch (error) {
          logger.error('Error fetching user data:', error);
          this.current_user = null;
        }
      }
    },
    async checkLoginStatus(token){
      try {
        const response = await axios.post(`${apiBaseUrl}/api/users/check_login_session`, {
          access_token: token
        });
        if (this._isSuccessResponse(response)) {
          logger.log('Check login session successfully');
          return true;
        } else {
          logger.error('Error checkLoginStatus:', response);
          return false;
        }
      } catch (error) {
        logger.error('Error checkLoginStatus:', error);
        return false;
      }
    },
    async signoutLogin(loginId) {
      if (this.isAuthenticated) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${this.token}`;
          await axios.post(`${apiBaseUrl}/api/users/me/signout`, {
            login_id: loginId
          });
        } catch (error) {
          logger.error('Error sign out:', error);
        }
      }
    },
    async signoutAll() {
      if (this.isAuthenticated) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${this.token}`;
          await axios.post(`${apiBaseUrl}/api/users/me/signout/all`);
        } catch (error) {
          logger.error('Error sign out:', error);
        }
        this.purgeUser();
        router.push({ name: 'SignIn' });
      }
    },
    purgeUser() {
      this.token = '';
      localStorage.removeItem('user-token');
      delete axios.defaults.headers.common['Authorization'];
      this.current_user = null;
      this.is_staff_verified = false;
    },
    logout() {
      this.signoutLogin();
      this.purgeUser();
      router.push({ name: 'SignIn' });
    },
    getFirstNameInitial() {
      return this.current_user?.first_name?.charAt(0) || "A";
    },
    getEmail() {
      return this.current_user?.email || "example@zyxel.com.tw";
    },
    getDomainByEmail() {
      return this.current_user?.email?.split('@')[1] || null;
    },
    async revokeAccessToken(token, logoutRedirectUri = null) {
      try {
        const payload = { token };
        if (logoutRedirectUri) payload.logout_redirect_uri = logoutRedirectUri;
        const response = await axios.post(`${apiBaseUrl}/api/oauth/access_token/revoke`, payload);
        if (this._isSuccessResponse(response)) {
          logger.log('Token revoked successfully');
          return {
            success: true,
            logoutRedirectUris: response.data?.logout_redirect_uris ?? [],
            allowedLogoutRedirectUri: response.data?.allowed_logout_redirect_uri ?? null,
          };
        } else {
          logger.error('Error revoking token:', response);
          return { success: false, logoutRedirectUris: [], allowedLogoutRedirectUri: null };
        }
      } catch (error) {
        logger.error('Error during token revocation:', error);
        return { success: false, logoutRedirectUris: [], allowedLogoutRedirectUri: null };
      }
    },
    async getAuthorizationCodeAndRedirect() {
      this._setAuthHeader();

      try {
        const redirectConfig = this._getRedirectConfig();

        if (redirectConfig.appId) {
          await this._handleAppRedirect(redirectConfig);
        } else if (redirectConfig.redirectUri) {
          await this._handleOAuthRedirect(redirectConfig);
        } else {
          this._handleLocalRedirect();
        }
      } catch (error) {
        logger.error('Error fetching authorization code:', error);
        this._handleOAuthError(error);
      }
    },

    _handleOAuthError(error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;

      // Clean up OAuth storage
      this._cleanupOAuthStorage();
      this._cleanupRedirectStorage();

      // RFC 6749 Section 4.1.2.1: Different handling for different errors
      if (status === 400 && (detail === 'invalid_client' || detail === 'invalid_redirect_uri')) {
        // CRITICAL: Cannot redirect to client - show dedicated error page
        // This prevents authorization code interception attacks
        router.push({
          name: 'OAuthError',
          query: {
            error_code: detail
          }
        });
      } else if (status === 401) {
        // Authentication failed - redirect to login
        this.purgeUser();
        router.push({
          name: 'SignIn',
          query: { error: 'session_expired' }
        });
      } else {
        // Other errors - show generic OAuth error
        router.push({
          name: 'OAuthError',
          query: {
            error_code: 'unknown'
          }
        });
      }
    },

    _setAuthHeader() {
      axios.defaults.headers.common['Authorization'] = `Bearer ${this.token}`;
    },

    _isSuccessResponse(response) {
      return response?.status === 200;
    },

    _isForbiddenError(error) {
      return error?.response?.status === 403;
    },

    _getRedirectConfig() {
      return {
        appId: localStorage.getItem('redirectAppId'),
        redirectUri: localStorage.getItem('redirect_uri'),
        clientId: localStorage.getItem('client_id'),
        responseType: localStorage.getItem('response_type'),
        state: localStorage.getItem('state')
      };
    },

    async _handleAppRedirect(config) {
      const fdlUrl = await getDynamicLinkById(config.appId);
      const directUrl = fdlUrl || config.redirectUri;

      if (!directUrl) {
        logger.error('No valid redirect URL found: fdlUrl and redirectUri are both null');
        return;
      }

      this._cleanupRedirectStorage();

      try {
        const endpoint = config.redirectUri ? 'access_token' : 'app_fdl_access_token';
        const payload = config.redirectUri
          ? { client_id: config.clientId, redirect_uri: config.redirectUri, response_type: config.responseType, state: config.state }
          : { response_type: 'token', state: config.state };

        const response = await axios.post(`${apiBaseUrl}/api/oauth/${endpoint}`, payload);

        if (response.data) {
          const finalUrl = this._buildAppRedirectUrl(directUrl, response.data, fdlUrl);
          if (finalUrl) {
            window.location.href = finalUrl;
          }
        }
      } catch (error) {
        logger.error('App redirect error:', error);
        // For app redirects, we cannot safely redirect to the app with errors
        // Re-throw to be handled by the top-level error handler
        throw error;
      }
    },

    async _handleOAuthRedirect(config) {
      const payload = {
        client_id: config.clientId,
        redirect_uri: config.redirectUri,
        response_type: config.responseType,
        state: config.state
      };

      const endpoint = config.responseType === 'code' ? 'code' : 'access_token';
      const response = await axios.post(`${apiBaseUrl}/api/oauth/${endpoint}`, payload);

      if (response.data) {
        this._cleanupOAuthStorage();
        const finalUrl = this._buildOAuthRedirectUrl(config.redirectUri, response.data, config.state);
        window.location.href = finalUrl;
      }
    },

    _handleLocalRedirect() {
      const postLoginRedirect = localStorage.getItem('postLoginRedirect');
      if (postLoginRedirect) {
        localStorage.removeItem('postLoginRedirect');
        const redirectPath = { 'Passkey': '/user/passkey' }[postLoginRedirect];

        if (redirectPath) {
          router.push(redirectPath);
          return;
        }
      }
      router.push({ name: 'UserInfoPage' });
    },

    _cleanupRedirectStorage() {
      localStorage.removeItem('appId');
      localStorage.removeItem('redirectAppId');
    },

    _cleanupOAuthStorage() {
      ['client_id', 'redirect_uri', 'response_type', 'state'].forEach(key => {
        localStorage.removeItem(key);
      });
    },

    _buildAppRedirectUrl(baseUrl, tokenData, fdlUrl) {
      if (!baseUrl) {
        logger.error('baseUrl is null or undefined in _buildAppRedirectUrl');
        return null;
      }

      const separator = baseUrl.includes('?') ? '&' : (fdlUrl ? '?' : '#');

      const params = tokenData.token_type
        ? `access_token=${encodeURIComponent(tokenData.access_token)}&token_type=${encodeURIComponent(tokenData.token_type)}&expires_in=${encodeURIComponent(tokenData.expires_in)}`
        : `access_token=${encodeURIComponent(tokenData.access_token)}`;

      return baseUrl + separator + params;
    },

    _buildOAuthRedirectUrl(redirectUri, responseData, state) {
      const url = new URL(redirectUri);

      if (responseData.access_token) {
        let hash = state ? `state=${encodeURIComponent(state)}&` : '';
        hash += `access_token=${encodeURIComponent(responseData.access_token)}&token_type=${encodeURIComponent(responseData.token_type)}&expires_in=${encodeURIComponent(responseData.expires_in)}`;
        url.hash = hash;
      } else {
        url.searchParams.append('code', responseData.code);
        url.searchParams.append('state', state);
      }

      return url.toString();
    },
    async updateUserProfile(updatedUserData) {
      if (!this.isAuthenticated) {
        logger.error('User is not authenticated');
        return;
      }

      try {
        const response = await axios.post(`${apiBaseUrl}/api/users/me`, updatedUserData);

        if (this._isSuccessResponse(response)) {
          await this.fetchCurrentUser();
          logger.log('User profile updated successfully');
        } else {
          logger.error('Error updating user profile:', response);
        }
      } catch (error) {
        logger.error('Error during user profile update:', error);
      }
    },
    async updateUserLanguage(updatedUserData) {
      if (!this.isAuthenticated) {
        logger.error('User is not authenticated');
        return { success: false, error: 'Not authenticated' };
      }

      try {
        const response = await axios.post(`${apiBaseUrl}/api/users/me/language_updating`, updatedUserData);

        if (this._isSuccessResponse(response)) {
          logger.log('User display language updated successfully');
          await this.fetchCurrentUser(); // 重新取得使用者資料
          return { success: true, language: response.data.display_language };
        } else {
          logger.error('Error updating user display language:', response);
          return { success: false, error: 'Update failed' };
        }
      } catch (error) {
        logger.error('Error during user display language update:', error);

        // 如果是語言驗證錯誤，fallback到英文
        if (error.response?.status === 422) {
          logger.warn('Unsupported language, falling back to English');
          try {
            const fallbackResponse = await axios.post(`${apiBaseUrl}/api/users/me/language_updating`,
              { displayLanguage: 'en' });

            if (this._isSuccessResponse(fallbackResponse)) {
              await this.fetchCurrentUser();
              return { success: true, language: 'en', fallback: true };
            }
          } catch (fallbackError) {
            logger.error('Fallback to English also failed:', fallbackError);
          }
        }

        return { success: false, error: error.response?.data?.detail || 'Unknown error' };
      }
    },
    async changeUserPassword(pwdData) {
      if (!this.isAuthenticated) {
        logger.error('User is not authenticated');
        return false;
      }

      try {
        const response = await axios.post(`${apiBaseUrl}/api/users/me/change_password`, pwdData);

        if (this._isSuccessResponse(response)) {
          await this.fetchCurrentUser();
          logger.log('User profile updated successfully');
          return true
        } else {
          logger.error('Error updating user profile:', response);
          return false
        }
      } catch (error) {
        logger.error('Error during user profile update:', error);
        return false
      }
    },
    async getNotifySettings() {
      if (!this.isAuthenticated) {
        logger.error('User is not authenticated');
        return;
      }

      try {
        const response = await axios.get(`${apiBaseUrl}/api/users/me/notify_setting`);

        if (this._isSuccessResponse(response)) {
          logger.log('Notify settings fetched successfully:', response.data);
          return response.data;
        } else {
          logger.error('Error fetching notify settings:', response);
        }
      } catch (error) {
        logger.error('Error during notify settings fetch:', error);
      }
    },
    async updateNotifySettings(updatedNotifySettings) {
      if (!this.isAuthenticated) {
        logger.error('User is not authenticated');
        return;
      }

      try {
        const response = await axios.post(`${apiBaseUrl}/api/users/me/notify_setting`, updatedNotifySettings);

        if (this._isSuccessResponse(response)) {
          logger.log('Notify settings updated successfully');
        } else {
          logger.error('Error updating notify settings:', response);
        }
      } catch (error) {
        logger.error('Error during notify settings update:', error);
      }
    },
    async getSubscribeSettings() {
      if (!this.isAuthenticated) {
        logger.error('User is not authenticated');
        return;
      }

      try {
        const response = await axios.get(`${apiBaseUrl}/api/users/me/subscribe_setting`);

        if (this._isSuccessResponse(response)) {
          logger.log('Subscribe settings fetched successfully:', response.data);
          return response.data;
        } else {
          logger.error('Error fetching Subscribe settings:', response);
        }
      } catch (error) {
        logger.error('Error during Subscribe settings fetch:', error);
      }
    },
    async updateSubscribeSettings(updatedSubscribeSettings) {
      if (!this.isAuthenticated) {
        logger.error('User is not authenticated');
        return;
      }

      try {
        const response = await axios.post(`${apiBaseUrl}/api/users/me/subscribe_setting`, updatedSubscribeSettings);

        if (this._isSuccessResponse(response)) {
          logger.log('Subscribe settings updated successfully');
        } else {
          logger.error('Error updating Subscribe settings:', response);
        }
      } catch (error) {
        logger.error('Error during Subscribe settings update:', error);
      }
    },
    async get2FASettings() {
      if (!this.isAuthenticated) {
        logger.error('User is not authenticated');
        return;
      }

      try {
        const response = await axios.get(`${apiBaseUrl}/api/users/2fa`);

        if (this._isSuccessResponse(response)) {
          logger.log('2FA settings fetched successfully:', response.data);
          return response.data;
        } else {
          logger.error('Error fetching 2FA settings:', response);
        }
      } catch (error) {
        logger.error('Error during 2FA settings fetch:', error);
      }
    },
    async getOtpSettings() {
      if (!this.isAuthenticated) {
        logger.error('User is not authenticated');
        return;
      }

      try {
        const response = await axios.get(`${apiBaseUrl}/api/users/2fa/otp`);

        if (this._isSuccessResponse(response)) {
          logger.log('Google OTP settings fetched successfully:', response.data);
          return response.data;
        } else {
          logger.error('Error fetching Google OTP settings:', response);
        }
      } catch (error) {
        logger.error('Error during Google OTP settings fetch:', error);
      }
    },
    async update2FAStatus(status) {
      if (!this.isAuthenticated) {
        logger.error('User is not authenticated');
        return;
      }

      try {
        const response = await axios.post(`${apiBaseUrl}/api/users/2fa/skip_remind`, { status: status });

        if (this._isSuccessResponse(response)) {
          logger.log('2FA status updated successfully');
        } else {
          logger.error('Error updating 2FA status:', response);
        }
      } catch (error) {
        logger.error('Error during 2FA status update:', error);
      }
    },
    async enable2FAOTP(backup_codes) {
      if (!this.isAuthenticated) {
        logger.error('User is not authenticated');
        return false;
      }
      const otpEnableUrl = `${apiBaseUrl}/api/users/2fa/otp/enable`;
      try {
        const response = await axios.post(otpEnableUrl, {backup_codes});
        if (this._isSuccessResponse(response)) {
          logger.log('Enable OTP successfully');
          return true;
        }
        logger.error('Enable OTP failed with status:', response?.status);
        return false;
      } catch (error) {
        logger.error('Error during enable OTP setting', error?.response?.status, error?.response?.data);
        return false;
      }
    },
    async verifyOTPPincode(pincode) {
      if (!this.isAuthenticated) {
        logger.error('User is not authenticated');
        return false;
      }

      const otpVerifyUrl = `${apiBaseUrl}/api/users/2fa/otp/verify`;
      const login_time = new Date().toString();  // local time
      try {
        const response = await axios.post(otpVerifyUrl, { pincode, login_time });

        if (this._isSuccessResponse(response)) {
          logger.log('OTP verification successful:', response.data);
          return true;
        }
      } catch (error) {
        logger.error('Error during OTP verification:', error);
        return false;
      }
    },
    async generateOPTBackupCodes() {
      if (!this.isAuthenticated) {
        logger.error('User is not authenticated');
        return false;
      }

      try {
        const response = await axios.post(`${apiBaseUrl}/api/users/2fa/otp/backup_codes`);
        if (this._isSuccessResponse(response)) {
          logger.log('OTP verification successful:', response.data);
          return response.data.otp_backup_codes;
        }
      } catch (error) {
        logger.error('Error during OTP generate new codes')
      }
    },
    async send2FAEmailVerification() {
      if (!this.isAuthenticated) {
        logger.error('User is not authenticated');
        return false;
      }

      try {
        const redirectAppId = localStorage.getItem('redirectAppId');
        const response = await axios.post(`${apiBaseUrl}/api/users/2fa/email/send_verification?_fdl_app_id=${encodeURIComponent(redirectAppId)}`);
        if (this._isSuccessResponse(response)) {
          logger.log('Send enable 2FA email verification successfully');
          return true;
        }
      } catch (error) {
        logger.error('Error during send enable 2FA email verification')
        return false;
      }
    },
    async send2FASigninEmail() {
      if (!this.isAuthenticated) {
        logger.error('User is not authenticated');
        router.push({ name: 'SignIn' });
        return false;
      }

      try {
        const appid = localStorage.getItem('appId');
        let urlWithQuery = `${apiBaseUrl}/api/users/2fa/email/send_signin`;
        if (appid && appid !== 'null') {
          urlWithQuery += `?_fdl_app_id=${encodeURIComponent(appid)}`;
        }
        const response = await axios.post(urlWithQuery);
        if (this._isSuccessResponse(response)) {
          logger.log('Send enable 2FA sign-in email successfully');
          return true;
        }
      } catch (error) {
        logger.error('Error during send 2FA sign-in email')
        return false;
      }
    },
    async disable2FA() {
      if (!this.isAuthenticated) {
        logger.error('User is not authenticated');
        return false;
      }

      try {
        const response = await axios.post(`${apiBaseUrl}/api/users/2fa/request_disable`);
        if (this._isSuccessResponse(response)) {
          logger.log('Send disable 2FA verification successfully');
          return true;
        }
      } catch (error) {
        logger.error('Error during send disable 2FA verification')
        return false;
      }
    },
    async sendChangeEmail(email) {
      if (!this.isAuthenticated) {
        logger.error('User is not authenticated');
        return false;
      }

      try {
        const response = await axios.post(`${apiBaseUrl}/api/users/change_email`, {
          email: email
        });
        if (this._isSuccessResponse(response)) {
          logger.log('Send change email verification successfully');
          return true;
        }
      } catch (error) {
        logger.error('Error during send change email email verification')
        return false;
      }
    },
    async disable2FAByPassword(password) {
      if (!this.isAuthenticated) {
        logger.error('User is not authenticated');
        return false;
      }

      try {
        const response = await axios.post(`${apiBaseUrl}/api/users/2fa/password/disable`, {
          password: password
        });

        if (this._isSuccessResponse(response)) {
          logger.log('2FA disabled successfully');
          return true;
        }
      } catch (error) {
        if (this._isForbiddenError(error)) {
          logger.error('Invalid password, unable to disable 2FA');
        } else {
          logger.error('Error during disabling 2FA');
        }
        return false;
      }
    },
    async checkAccessTokenAndRedirect(access_token) {
      try {
        let redirect_uri = localStorage.getItem('redirect_uri');
        let response_type = localStorage.getItem('response_type');
        if (!redirect_uri){
          return false;
        }
        const valid_response = await axios.post(`${apiBaseUrl}/api/email_token/access_token_valid`, {
          access_token: access_token
        });
        if (this._isSuccessResponse(valid_response)) {
          await this.authenticateUser(valid_response.data.web_session_token);
          let payload = {
            client_id: localStorage.getItem('client_id'),
            redirect_uri: localStorage.getItem('redirect_uri'),
            response_type: localStorage.getItem('response_type'),
            state: ''
          };
          const access_token_response = await axios.post(`${apiBaseUrl}/api/oauth/access_token`, payload);
          if (access_token_response.data) {
            localStorage.removeItem('client_id');
            localStorage.removeItem('redirect_uri');
            localStorage.removeItem('response_type');
            const redirectUri = new URL(redirect_uri);
            let hash = '#';
            hash += `access_token=${encodeURIComponent(access_token_response.data.access_token)}&token_type=${encodeURIComponent(response_type)}&expires_in=${encodeURIComponent(access_token_response.data.expires_in)}`;
            redirectUri.hash = hash;
            window.location.href = redirectUri.toString();
            return true;
          }
        }
      } catch (error) {
        if (error && error.response) {
          logger.error('Invalid Access Token' + error.response);
        } else {
          logger.error('Invalid Access Token');
        }
        return false;
      }
    },
    async getEntraSetting() {
      if (!this.isAuthenticated) {
        logger.error('User is not authenticated');
        return;
      }
      try {
        const response = await axios.get(`${apiBaseUrl}/api/entra_id`);

        if (this._isSuccessResponse(response)) {
          logger.log('Entra ID settings fetched successfully:', response.data);
          return response.data;
        } else {
          logger.error('Error fetching Entra ID settings:', response);
        }
      } catch (error) {
        logger.error('Error during Entra ID settings fetch:', error);
      }
    },
    async enablePasswordless() {
      if (!this.isAuthenticated) {
        logger.error('User is not authenticated');
        return { success: false, error: 'Not authenticated' };
      }

      try {
        const response = await axios.post(`${apiBaseUrl}/api/passkey/enable-passwordless`);

        if (this._isSuccessResponse(response)) {
          logger.log('Passwordless mode enabled successfully');
          await this.fetchCurrentUser(); // Refresh user data to update passwordless_status
          return { success: true, data: response.data };
        } else {
          logger.error('Error enabling passwordless mode:', response);
          return { success: false, error: 'Enable failed' };
        }
      } catch (error) {
        logger.error('Error during enabling passwordless mode:', error);

        // Handle specific error codes
        const errorCode = error.response?.data?.code;
        const errorMsg = error.response?.data?.msg;

        if (errorCode === 1002) {
          return { success: false, error: 'Current login method is not password', code: 1002 };
        }

        return { success: false, error: errorMsg || 'Unknown error', code: errorCode };
      }
    },
  },
});
